import { EditorState, SelectionState } from 'draft-js';
import DraftOffsetKey from 'draft-js/lib/DraftOffsetKey';
import findAncestorOffsetKey from 'draft-js/lib/findAncestorOffsetKey';
import getUpdatedSelectionState from 'draft-js/lib/getUpdatedSelectionState';
import { PureComponent } from 'react';

import { getCaretPositionFromPoint } from 'my-core/editor-utils';

import { pluginPresets, SolutionStepsEditor } from 'my-editor';
import Editor from 'my-editor/EditorError';

// `pluginPresets.ALL` deliberately excludes `table`, so cells can hold every other kind of content
// (equations, images, videos, links, FITB) without allowing tables to nest inside themselves.
const CELL_PLUGINS = pluginPresets.ALL;

export default class TableCell extends PureComponent {
  componentDidMount() {
    // A cell can mount already active when Tab appends a row or a column is inserted.
    if (this.props.active) this.focusEditor(this.props.focusPosition);
  }

  componentDidUpdate(prevProps) {
    const { active, focusPosition } = this.props;
    if (active && (!prevProps.active || focusPosition !== prevProps.focusPosition)) {
      // A cell activated by a click already knows where the caret belongs, which beats the edge
      // position that Tab and the arrow keys ask for.
      this.focusEditor(this.clickedSelection || focusPosition);
      this.clickedSelection = null;
    } else if (prevProps.active && !active) {
      this.editor?.releaseFocus();
    }
  }

  setEditorRef = editor => {
    this.editor = editor;
  };

  getEditorState() {
    return this.editor?.state?.editorState;
  }

  // Forcing the selection (rather than only calling focus) also pushes the cell's editorState up to
  // the shared toolbar, so the formatting controls retarget onto this cell immediately.
  // `position` is either an edge name ('start' / 'end') or a resolved SelectionState.
  focusEditor(position) {
    if (!this.editor) return;
    const pos = position || 'end';

    // Focus first: on an editor that isn't focused yet, Draft's own `focus()` restores whatever
    // selection the cell was last left with, which would override the position we want.
    this.editor.focus();

    const editorState = this.getEditorState();
    if (editorState) {
      const selection = typeof pos === 'string' ? getEdgeSelection(editorState, pos) : pos;
      this.editor.handleEditorStateChange(EditorState.forceSelection(editorState, selection));
    }
  }

  // Draft never sees the mousedown that activates a cell: the editor is still read only at that
  // point and the handler cancels the event anyway, to keep the browser from dropping the caret into
  // the surrounding non-editable markup. So resolve the click the same way Draft resolves a native
  // one, while the DOM still matches the editorState we are reading it against.
  getSelectionFromPoint(x, y) {
    const editorState = this.getEditorState();
    const caret = editorState && getCaretPositionFromPoint(x, y);
    const offsetKey = caret && findAncestorOffsetKey(caret.node);
    if (!offsetKey) return null;

    // A cell can host nested editors (a callout, say), whose leaves carry offset keys from a content
    // state of their own. Those clicks are not ours to place.
    const block = editorState.getCurrentContent().getBlockForKey(DraftOffsetKey.decode(offsetKey).blockKey);
    if (!block) return null;

    const selection = getUpdatedSelectionState(editorState, offsetKey, caret.offset, offsetKey, caret.offset);
    // Clicking a decorator's own markup (the innards of a rendered equation, say) can resolve past
    // the end of the block's text, and Draft quietly declines to place a caret there.
    const offset = Math.min(selection.getStartOffset(), block.getLength());
    return SelectionState.createEmpty(block.getKey()).merge({ anchorOffset: offset, focusOffset: offset });
  }

  // The vertical arrows hand off as soon as the caret sits in the edge block, since there is no line
  // left to move onto within the cell. The horizontal ones additionally require the caret to be at
  // the very edge of that block's text, so they keep stepping through the characters first.
  isCaretAtEdge(edge, requireOffset) {
    const editorState = this.getEditorState();
    if (!editorState) return true;

    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) return false;

    const content = editorState.getCurrentContent();
    const edgeBlock = edge === 'last' ? content.getLastBlock() : content.getFirstBlock();
    if (selection.getStartKey() !== edgeBlock.getKey()) return false;

    return !requireOffset || selection.getStartOffset() === (edge === 'last' ? edgeBlock.getLength() : 0);
  }

  handleActivate = e => {
    const { columnIndex, onActivate, rowIndex } = this.props;
    this.clickedSelection = this.getSelectionFromPoint(e.clientX, e.clientY);
    e.preventDefault();
    onActivate(rowIndex, columnIndex);
  };

  handleContentChange = content => {
    const { columnIndex, onChange, rowIndex } = this.props;
    onChange(rowIndex, columnIndex, content);
  };

  // Runs in the capture phase so Tab and the arrows are consumed before Draft's own keyBindingFn
  // sees them. Passing keyBindingFn down to the nested <Editor> instead would clobber its bindings,
  // since `{...rest}` is spread last in Editor.jsx.
  handleKeyDownCapture = e => {
    const { active, columnIndex, onNavigate, rowIndex } = this.props;
    if (!active) return;

    let direction = null;
    if (e.key === 'Tab') {
      direction = e.shiftKey ? 'previous' : 'next';
    } else if (e.key === 'ArrowUp' && this.isCaretAtEdge('first')) {
      direction = 'up';
    } else if (e.key === 'ArrowDown' && this.isCaretAtEdge('last')) {
      direction = 'down';
    } else if (e.key === 'ArrowLeft' && this.isCaretAtEdge('first', true)) {
      direction = 'previous';
    } else if (e.key === 'ArrowRight' && this.isCaretAtEdge('last', true)) {
      direction = 'next';
    }
    if (!direction) return;

    e.preventDefault();
    e.stopPropagation();
    // Only Tab grows the table off the end; an arrow key should stay put once it runs out of cells.
    onNavigate(rowIndex, columnIndex, direction, e.key === 'Tab');
  };

  render() {
    const {
      active,
      content,
      onToggleNestedEditorFocus,
      onToggleSolutionKey,
      onUpdateToolbarProps,
      readOnly,
      reportLatexErrors,
      showSolutions,
    } = this.props;

    if (readOnly && onToggleSolutionKey) {
      return (
        <SolutionStepsEditor onToggleSolutionKey={onToggleSolutionKey} showSolutions={showSolutions} value={content} />
      );
    }

    return (
      <div onKeyDownCapture={readOnly ? undefined : this.handleKeyDownCapture}>
        <Editor
          canToggleSolutions
          onChange={readOnly ? undefined : this.handleContentChange}
          onMouseDown={readOnly || active ? undefined : this.handleActivate}
          onToggleNestedEditorFocus={onToggleNestedEditorFocus}
          onUpdateToolbarProps={active ? onUpdateToolbarProps : undefined}
          plugins={CELL_PLUGINS}
          readOnly={readOnly || !active}
          ref={this.setEditorRef}
          reportLatexErrors={reportLatexErrors}
          showSolutions={!readOnly || showSolutions}
          value={content}
        />
      </div>
    );
  }
}

function getEdgeSelection(editorState, edge) {
  const content = editorState.getCurrentContent();
  const block = edge === 'start' ? content.getFirstBlock() : content.getLastBlock();
  const offset = edge === 'start' ? 0 : block.getLength();
  return SelectionState.createEmpty(block.getKey()).merge({ anchorOffset: offset, focusOffset: offset });
}
